import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";
import Select from "react-select";
import { getNotificationTemplates } from "../../api/getNotficationTemplate";
import { getAllUsers } from "../../api/getAllUser";
import { _notificationTemplate } from "../../constants/api";

const NotificationListScreen = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [userOptions, setUserOptions] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await getNotificationTemplates();
        if (response?.length > 0) {
          setNotifications(response);
        }
        setLoading(false);
      } catch (error) {
        setError(error.message);
        setLoading(false);
      }
    };

    const fetchUsers = async () => {
      try {
        const res = await getAllUsers();
        const formatted = res.map((user) => ({
          label: user.label || user.email || user._id,
          value: user._id,
        }));
        setUserOptions(formatted);
      } catch (err) {
        console.error("Failed to fetch users", err);
      }
    };

    setLoading(true);
    fetchNotifications();
    fetchUsers();
  }, []);

  const deleteHandler = async (id) => {
    if (window.confirm("Are you sure you want to delete this notification?")) {
      try {
        const response = await fetch(`${_notificationTemplate}/${id}`, {
          method: "DELETE",
        });
        if (!response.ok) {
          throw new Error("Failed to delete the notification");
        }
        setNotifications(
          notifications.filter((notification) => notification._id !== id)
        );
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const openEditDialog = (notification) => {
    setSelectedNotification(notification);
    setSelectedUsers(notification.userId.map((id) => ({
      value: id,
      label: id
    })));
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const updatedNotification = {
        ...selectedNotification,
        userId: selectedUsers.map((user) => user.value),
      };

      const response = await fetch(`${_notificationTemplate}/${selectedNotification._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedNotification),
      });

      if (!response.ok) throw new Error("Failed to update notification");

      const updatedList = notifications.map((n) =>
        n._id === selectedNotification._id ? updatedNotification : n
      );
      setNotifications(updatedList);
      setEditDialogOpen(false);
    } catch (err) {
      alert(err.message);
    }
  };

  const getDayNames = (days) => {
    const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    return days.map((day) => dayNames[day]).join(", ");
  };

  const getMonthNames = (months) => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months.map((month) => monthNames[month - 1]).join(", ");
  };

  const createNotificationHandler = () => {
    navigate("/create");
  };

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between" mb={2}>
        <Grid item>
          <Typography variant="h4">Notification Templates</Typography>
        </Grid>
        <Grid item>
          {!loading && (
            <Button variant="contained" color="primary" onClick={createNotificationHandler}>
              Create Template
            </Button>
          )}
        </Grid>
      </Grid>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          <TableContainer component={Paper} style={{ maxHeight: "600px" }}>
            <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 200 }}>TITLE</TableCell>
                  <TableCell sx={{ width: '400px' }}>TEXT CONTENT</TableCell>
                  <TableCell sx={{ width: 100 }}>FROM DATE</TableCell>
                  <TableCell sx={{ width: 100 }}>TO DATE</TableCell>
                  <TableCell sx={{ width: 100 }}>TRIGGER TIME</TableCell>
                  <TableCell sx={{ width: 100 }}>DATES TO SHOW</TableCell>
                  <TableCell sx={{ width: 100 }}>DAYS</TableCell>
                  <TableCell sx={{ width: 100 }}>MONTHS</TableCell>
                  <TableCell>ACTION</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.map((notification) => (
                  <TableRow key={notification._id}>
                    <TableCell>{notification.title}</TableCell>
                    <TableCell sx={{ width: "400px", whiteSpace: "normal" }}>
                      {notification.textContent}
                    </TableCell>
                    <TableCell>{notification.fromDate}</TableCell>
                    <TableCell>{notification.toDate}</TableCell>
                    <TableCell>{notification.triggerTime}</TableCell>
                    <TableCell>{notification.dateToShow[0]}</TableCell>
                    <TableCell>{getDayNames(notification.triggerDays)}</TableCell>
                    <TableCell>{getMonthNames(notification.triggerMonth)}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => openEditDialog(notification)}
                        style={{ marginRight: 8 }}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => deleteHandler(notification._id)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div>{'Notification count: ' + notifications?.length}</div>
          <h6>{' * Maximum Notification count should be less than or equal to 100'}</h6>
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Edit Notification</DialogTitle>
        <DialogContent sx={{height: 900}}>
          <TextField
            fullWidth
            margin="dense"
            label="Title"
            disabled
            value={selectedNotification?.title || ""}
            onChange={(e) => setSelectedNotification({ ...selectedNotification, title: e.target.value })}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Text Content"
            multiline
            minRows={3}
            value={selectedNotification?.textContent || ""}
            onChange={(e) => setSelectedNotification({ ...selectedNotification, textContent: e.target.value })}
          />
          <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>Users</Typography>
          <Select
            isMulti
            options={userOptions}
            value={selectedUsers}
            onChange={setSelectedUsers}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default NotificationListScreen;
